import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';

// BackButton component
const BackButton = () => {
  const navigate = useNavigate();
  
  const handleBackClick = () => {
    // Navigate back to the tournament review page
    navigate('/mecz/turniej-rankingowy/przeglad');
  };
  
  return (
    <button
      onClick={handleBackClick}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none'
      }}
      onMouseOver={(e) => e.target.style.color = '#333'}
      onMouseOut={(e) => e.target.style.color = '#888'}
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const Breadcrumbs = () => {
  const location = window.location.pathname;
  const segments = location.split('/').filter(seg => seg);
  
  const crumbs = segments.map(seg => {
    const decodedSeg = decodeURIComponent(seg);
    return decodedSeg;
  });
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        let displayText;
        if (idx === 0 && seg === 'mecz') {
          displayText = 'menu';
        } else if (seg === 'turniej-rankingowy') {
          displayText = 'turniej rankingowy';
        } else if (seg === 'przeglad') {
          displayText = 'przegląd turniejów';
        } else if (seg === 'rundy') {
          displayText = 'rundy';
        } else if (seg === 'mecz') {
          // Skip the intermediate 'mecz' segment
          return null;
        } else if (!isNaN(seg)) {
          // If it's a number (tournament ID), skip it
          return null;
        } else {
          displayText = seg;
        }
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            {displayText}
          </span>
        );
      }).filter(Boolean)}
    </div>
  );
};

const FinishedTournamentDetails = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [tournament, setTournament] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingGameId, setEditingGameId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    roundtype: '',
    playerscore: '',
    opponentscore: '',
    opponent1: '',
    opponent2: '',
    opponent3: '',
    opponent4: ''
  });

  useEffect(() => {
    const fetchTournamentData = async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        if (!token) {
          throw new Error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
        }
        
        // Fetch tournament details
        const tournamentResponse = await fetch(`/api/tournaments/${tournamentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!tournamentResponse.ok) {
          throw new Error(`Błąd ${tournamentResponse.status}: ${await tournamentResponse.text()}`);
        }
        
        const currentTournament = await tournamentResponse.json();
        setTournament(currentTournament);
        
        // Fetch games for this tournament
        const gamesResponse = await fetch(`/api/tournaments/${tournamentId}/games`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!gamesResponse.ok) {
          throw new Error(`Błąd ${gamesResponse.status}: ${await gamesResponse.text()}`);
        }
        
        const gamesData = await gamesResponse.json();
        setGames(gamesData);
        setLoading(false);
        
      } catch (err) {
        console.error('Fetch error:', err);
        setError(`Błąd połączenia: ${err.message}`);
        setLoading(false);
      }
    };

    fetchTournamentData();
  }, [tournamentId]);

  // Refresh data when component becomes visible (e.g., returning from add round page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && tournamentId) {
        fetchTournamentData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [tournamentId]);

  const fetchTournamentData = async () => {
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) return;
      
      // Fetch tournament details
      const tournamentResponse = await fetch(`/api/tournaments/${tournamentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (tournamentResponse.ok) {
        const currentTournament = await tournamentResponse.json();
        setTournament(currentTournament);
      }
      
      // Fetch games for this tournament
      const gamesResponse = await fetch(`/api/tournaments/${tournamentId}/games`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (gamesResponse.ok) {
        const gamesData = await gamesResponse.json();
        setGames(gamesData);
      }
      
    } catch (err) {
      console.error('Refresh error:', err);
    }
  };

  const handleDeleteClick = (game) => {
    setGameToDelete(game);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!gameToDelete) return;
    
    setDeleting(true);
    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) {
        throw new Error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
      }
      
      const response = await fetch(`/api/tournaments/games/${gameToDelete.game_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Błąd ${response.status}: ${await response.text()}`);
      }
      
      // Refresh games list
      await fetchTournamentData();
      
      setShowDeleteModal(false);
      setGameToDelete(null);
      
    } catch (err) {
      console.error('Delete error:', err);
      setError(`Błąd podczas usuwania meczu: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setGameToDelete(null);
  };

  const handleEditClick = (game) => {
    setEditingGameId(game.game_id);
    setEditFormData({
      roundtype: game.roundtype || '',
      playerscore: game.playerscore || '',
      opponentscore: game.opponentscore || '',
      opponent1: game.opponent1 || '',
      opponent2: game.opponent2 || '',
      opponent3: game.opponent3 || '',
      opponent4: game.opponent4 || ''
    });
    setEditing(false);
  };

  const handleEditCancel = () => {
    setEditing(false);
    setEditingGameId(null);
    setEditFormData({
      roundtype: '',
      playerscore: '',
      opponentscore: '',
      opponent1: '',
      opponent2: '',
      opponent3: '',
      opponent4: ''
    });
  };

  const handleEditSubmit = async () => {
    if (!editingGameId) return;
    
    // Validation
    if (!editFormData.roundtype) {
      setError('Wybierz typ rundy');
      return;
    }
    
    if (!editFormData.playerscore || !editFormData.opponentscore) {
      setError('Wprowadź wyniki dla obu drużyn');
      return;
    }
    
    const playerScore = parseInt(editFormData.playerscore);
    const opponentScore = parseInt(editFormData.opponentscore);
    
    if (isNaN(playerScore) || isNaN(opponentScore)) {
      setError('Wyniki muszą być liczbami');
      return;
    }
    
    if (playerScore < 0 || playerScore > 13 || opponentScore < 0 || opponentScore > 13) {
      setError('Wyniki muszą być między 0 a 13');
      return;
    }
    
    if (playerScore === opponentScore) {
      setError('W petanque nie ma remisów! Wyniki obu drużyn muszą się różnić.');
      return;
    }
    
    setEditing(true);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('access_token');
      
      const updateData = {
        roundtype: editFormData.roundtype,
        playerscore: playerScore,
        opponentscore: opponentScore,
        opponent1: editFormData.opponent1.trim() || null,
        opponent2: editFormData.opponent2.trim() || null,
        opponent3: editFormData.opponent3.trim() || null,
        opponent4: editFormData.opponent4.trim() || null
      };
      
      const response = await fetch(`/api/tournaments/games/${editingGameId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Błąd ${response.status}: ${errorText}`);
      }
      
      // Update the game in the local state
      setGames(prevGames => 
        prevGames.map(game => 
          game.game_id === editingGameId 
            ? { ...game, ...updateData }
            : game
        )
      );
      
      // Exit edit mode and reset state
      setEditingGameId(null);
      
    } catch (err) {
      console.error('Edit error:', err);
      setError(`Błąd podczas edytowania rundy: ${err.message}`);
    } finally {
      setEditing(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  const getTournamentType = (numberofplayers) => {
    switch (numberofplayers) {
      case 1: return 'Single';
      case 2: return 'Dublety';
      case 3: return 'Triplety';
      case 4: return 'Triplety z rezerwowym';
      default: return 'Nieznany';
    }
  };

  // Helper function to determine which opponent fields to show based on tournament type
  const getOpponentFieldsConfig = (numberofplayers) => {
    switch (numberofplayers) {
      case 1: // Single
        return {
          showOpponent1: true,
          showOpponent2: false,
          showOpponent3: false,
          showOpponent4: false,
          opponent4Label: 'Rezerwowy'
        };
      case 2: // Dublety
        return {
          showOpponent1: true,
          showOpponent2: true,
          showOpponent3: false,
          showOpponent4: false,
          opponent4Label: 'Rezerwowy'
        };
      case 3: // Triplety
        return {
          showOpponent1: true,
          showOpponent2: true,
          showOpponent3: true,
          showOpponent4: false,
          opponent4Label: 'Rezerwowy'
        };
      case 4: // Triplety z rezerwowym
        return {
          showOpponent1: true,
          showOpponent2: true,
          showOpponent3: true,
          showOpponent4: true,
          opponent4Label: 'Rezerwowy'
        };
      default:
        return {
          showOpponent1: false,
          showOpponent2: false,
          showOpponent3: false,
          showOpponent4: false,
          opponent4Label: 'Rezerwowy'
        };
    }
  };

  if (loading) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Ładowanie danych...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <h2 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.5rem'
            }}>
              Błąd ładowania turnieju
            </h2>
            <p style={{ color: 'red', fontFamily: 'Nunito, Arial, sans-serif', marginBottom: '20px' }}>
              {error}
            </p>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontSize: '14px'
              }}
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Turniej nie został znaleziony.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        ShootMe
      </h1>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>

      {/* Tournament Info */}
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          marginBottom: '20px'
        }}>
          <h2 style={{
            color: '#1e3a8a',
            fontFamily: 'Nunito, Arial, sans-serif',
            marginBottom: '20px',
            fontSize: '1.5rem',
            textAlign: 'center'
          }}>
            Szczegóły turnieju
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 600 ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            fontFamily: 'Nunito, Arial, sans-serif'
          }}>
            <div>
              <strong>Data:</strong> {formatDate(tournament.tournamentdate)}
            </div>
            <div>
              <strong>Nazwa:</strong> {tournament.tournamentname || '-'}
            </div>
            <div>
              <strong>Miasto:</strong> {tournament.city || '-'}
            </div>
            <div>
              <strong>Typ:</strong> {getTournamentType(tournament.numberofplayers)}
            </div>
            <div>
              <strong>Ilość graczy:</strong> {tournament.totalplayers || '-'}
            </div>
            <div>
              <strong>Miejsce:</strong> {tournament.place || '-'}
            </div>
            {tournament.player2 && (
              <div>
                <strong>{getTranslation('gracz2', language)}:</strong> {tournament.player2}
              </div>
            )}
            {tournament.player3 && (
              <div>
                <strong>{getTranslation('gracz3', language)}:</strong> {tournament.player3}
              </div>
            )}
            {tournament.player4 && (
              <div>
                <strong>Rezerwowy:</strong> {tournament.player4}
              </div>
            )}
          </div>
        </div>

        {/* Games List */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{
            color: '#1e3a8a',
            fontFamily: 'Nunito, Arial, sans-serif',
            marginBottom: '20px',
            fontSize: '1.5rem',
            textAlign: 'center'
          }}>
            Rundy
          </h2>
          
          {games.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ 
                fontFamily: 'Nunito, Arial, sans-serif', 
                fontSize: '1.2rem', 
                color: '#666' 
              }}>
                Brak rund w tym turnieju
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: 'Nunito, Arial, sans-serif'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Edytuj</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Usuń</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Runda</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Typ</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Wynik</th>
                    {(() => {
                      const config = getOpponentFieldsConfig(tournament.numberofplayers);
                      return (
                        <>
                          {config.showOpponent1 && (
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Przeciwnik 1</th>
                          )}
                          {config.showOpponent2 && (
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Przeciwnik 2</th>
                          )}
                          {config.showOpponent3 && (
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Przeciwnik 3</th>
                          )}
                          {config.showOpponent4 && (
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{config.opponent4Label}</th>
                          )}
                        </>
                      );
                    })()}
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {games.map((game) => {
                    const isEditing = editingGameId === game.game_id;
                    
                    // Determine row color based on score comparison
                    const playerScore = parseInt(game.playerscore) || 0;
                    const opponentScore = parseInt(game.opponentscore) || 0;
                    
                    let rowStyle = { borderBottom: '1px solid #dee2e6' };
                    
                    if (playerScore > opponentScore) {
                      // Win - green background
                      rowStyle.backgroundColor = '#f0fdf4';
                      rowStyle.borderLeft = '4px solid #22c55e';
                    } else if (playerScore < opponentScore) {
                      // Loss - red background
                      rowStyle.backgroundColor = '#fef2f2';
                      rowStyle.borderLeft = '4px solid #ef4444';
                    }
                    
                    if (isEditing) {
                      rowStyle.backgroundColor = '#fef3c7';
                      rowStyle.borderLeft = '4px solid #f59e0b';
                    }
                    
                    return (
                      <tr key={game.game_id} style={rowStyle}>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleEditSubmit();
                                }}
                                disabled={editing}
                                style={{
                                  background: editing ? '#9ca3af' : '#22c55e',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  cursor: editing ? 'not-allowed' : 'pointer',
                                  fontFamily: 'Nunito, Arial, sans-serif',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                {editing ? '...' : '✓'}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleEditCancel();
                                }}
                                disabled={editing}
                                style={{
                                  background: editing ? '#9ca3af' : '#6b7280',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  cursor: editing ? 'not-allowed' : 'pointer',
                                  fontFamily: 'Nunito, Arial, sans-serif',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEditClick(game)}
                              style={{
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontFamily: 'Nunito, Arial, sans-serif',
                                fontSize: '12px',
                                fontWeight: '600',
                                transition: 'background 0.2s'
                              }}
                              onMouseOver={(e) => e.target.style.background = '#2563eb'}
                              onMouseOut={(e) => e.target.style.background = '#3b82f6'}
                            >
                              Edytuj
                            </button>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleDeleteClick(game)}
                            disabled={isEditing}
                            style={{
                              background: isEditing ? '#9ca3af' : '#ef4444',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: isEditing ? 'not-allowed' : 'pointer',
                              fontFamily: 'Nunito, Arial, sans-serif',
                              fontSize: '12px',
                              fontWeight: '600',
                              transition: 'background 0.2s'
                            }}
                            onMouseOver={(e) => !isEditing && (e.target.style.background = '#dc2626')}
                            onMouseOut={(e) => !isEditing && (e.target.style.background = '#ef4444')}
                          >
                            Usuń
                          </button>
                        </td>
                        <td style={{ padding: '12px' }}>{game.round || '-'}</td>
                        
                        {/* Round Type */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            <select
                              value={editFormData.roundtype}
                              onChange={(e) => handleEditInputChange('roundtype', e.target.value)}
                              style={{
                                padding: '6px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontFamily: 'Nunito, Arial, sans-serif',
                                fontSize: '12px',
                                width: '100%'
                              }}
                            >
                              <option value="">Wybierz typ</option>
                              <option value="B">Runda Buchholza</option>
                              <option value="P">Runda pucharowa</option>
                              <option value="G">Runda grupowa</option>
                            </select>
                          ) : (
                            game.roundtype || '-'
                          )}
                        </td>
                        
                        {/* Score */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <input
                                type="number"
                                value={editFormData.playerscore}
                                onChange={(e) => handleEditInputChange('playerscore', e.target.value)}
                                min="0"
                                max="13"
                                style={{
                                  padding: '4px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  fontFamily: 'Nunito, Arial, sans-serif',
                                  fontSize: '12px',
                                  width: '50px'
                                }}
                              />
                              <span>-</span>
                              <input
                                type="number"
                                value={editFormData.opponentscore}
                                onChange={(e) => handleEditInputChange('opponentscore', e.target.value)}
                                min="0"
                                max="13"
                                style={{
                                  padding: '4px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  fontFamily: 'Nunito, Arial, sans-serif',
                                  fontSize: '12px',
                                  width: '50px'
                                }}
                              />
                            </div>
                          ) : (
                            `${game.playerscore || 0} - ${game.opponentscore || 0}`
                          )}
                        </td>
                        
                        {/* Opponent Fields */}
                        {(() => {
                          const config = getOpponentFieldsConfig(tournament.numberofplayers);
                          return (
                            <>
                              {config.showOpponent1 && (
                                <td style={{ padding: '12px' }}>
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={editFormData.opponent1}
                                      onChange={(e) => handleEditInputChange('opponent1', e.target.value)}
                                      maxLength={30}
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontFamily: 'Nunito, Arial, sans-serif',
                                        fontSize: '12px',
                                        width: '100%'
                                      }}
                                    />
                                  ) : (
                                    game.opponent1 || '-'
                                  )}
                                </td>
                              )}
                              {config.showOpponent2 && (
                                <td style={{ padding: '12px' }}>
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={editFormData.opponent2}
                                      onChange={(e) => handleEditInputChange('opponent2', e.target.value)}
                                      maxLength={30}
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontFamily: 'Nunito, Arial, sans-serif',
                                        fontSize: '12px',
                                        width: '100%'
                                      }}
                                    />
                                  ) : (
                                    game.opponent2 || '-'
                                  )}
                                </td>
                              )}
                              {config.showOpponent3 && (
                                <td style={{ padding: '12px' }}>
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={editFormData.opponent3}
                                      onChange={(e) => handleEditInputChange('opponent3', e.target.value)}
                                      maxLength={30}
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontFamily: 'Nunito, Arial, sans-serif',
                                        fontSize: '12px',
                                        width: '100%'
                                      }}
                                    />
                                  ) : (
                                    game.opponent3 || '-'
                                  )}
                                </td>
                              )}
                              {config.showOpponent4 && (
                                <td style={{ padding: '12px' }}>
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={editFormData.opponent4}
                                      onChange={(e) => handleEditInputChange('opponent4', e.target.value)}
                                      maxLength={30}
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontFamily: 'Nunito, Arial, sans-serif',
                                        fontSize: '12px',
                                        width: '100%'
                                      }}
                                    />
                                  ) : (
                                    game.opponent4 || '-'
                                  )}
                                </td>
                              )}
                            </>
                          );
                        })()}
                        
                        <td style={{ padding: '12px' }}>{formatDate(game.gamedate)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>


      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.3rem'
            }}>
              Potwierdzenie usunięcia
            </h3>
            <p style={{
              color: '#374151',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '30px',
              fontSize: '1rem',
              lineHeight: '1.5'
            }}>
              Czy na pewno chcesz usunąć ten mecz?
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleDeleteCancel}
                disabled={deleting}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '14px',
                  fontWeight: '600',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                Nie
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                style={{
                  background: deleting ? '#9ca3af' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {deleting ? 'Usuwanie...' : 'Tak'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinishedTournamentDetails;
